import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import ManualAssessmentModal from './ManualAssessmentModal';
import AiAssessmentProposalCard from './AiAssessmentProposalCard';

/**
 * Assessment Manager Component
 * Manages both manual and AI-suggested assessments
 */
const AssessmentManager = ({
  conversationState,
  token,
  suggestionState,
  modalState,
  clientName,
  onSuccess,
  onError
}) => {
  // Assessment state
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [selectedAssessmentKey, setSelectedAssessmentKey] = useState('');
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState('');
  const [assessmentActionLoading, setAssessmentActionLoading] = useState(false);
  const [assessmentActionError, setAssessmentActionError] = useState('');

  // Load available assessments on mount
  useEffect(() => {
    const loadAssessments = async () => {
      try {
        const response = await api.listTestDefinitions(token);
        setAvailableAssessments(response.data || []);
      } catch (err) {
        console.error('Error loading assessments:', err);
        setAvailableAssessments([]);
      }
    };
    
    if (token) {
      loadAssessments();
    }
  }, [token]);

  // Handle manual assessment submission
  const handleSubmitAssessment = useCallback(async () => {
    if (!selectedAssessmentKey || !conversationState.selectedConversation) return;
    
    setAssessmentLoading(true);
    setAssessmentError('');
    
    try {
      await api.therapistInitiateAssessment(
        conversationState.selectedConversation._id, 
        selectedAssessmentKey, 
        token
      );
      onSuccess('Assessment initiated successfully');
      modalState.closeModal('manualAssessment');
      setSelectedAssessmentKey('');
    } catch (err) {
      console.error('Error initiating assessment:', err);
      setAssessmentError(err.message || 'Failed to initiate assessment');
    } finally {
      setAssessmentLoading(false);
    }
  }, [selectedAssessmentKey, conversationState.selectedConversation, token, modalState, onSuccess]);

  // Handle AI assessment proposal forward
  const handleForwardAssessment = useCallback(async () => {
    // Guard against double-fire: a single click can dispatch two POSTs before
    // the disabled={actionLoading} prop takes effect on the button. The
    // synthetic /initiate-assessment then runs the persona through the
    // assessment twice.
    if (assessmentActionLoading) return;
    setAssessmentActionLoading(true);
    setAssessmentActionError('');
    try {
      const assessmentKey = suggestionState.suggestionState.assessment?.assessmentDetails?.testKey;
      if (assessmentKey) {
        await api.therapistInitiateAssessment(
          conversationState.selectedConversation._id,
          assessmentKey,
          token
        );
        onSuccess('Assessment forwarded to client successfully');
        suggestionState.clearAssessmentSuggestion();
      }
    } catch (err) {
      console.error('Error forwarding assessment:', err);
      setAssessmentActionError(err.message || 'Failed to forward assessment');
      onError(err.message || 'Failed to forward assessment');
    } finally {
      setAssessmentActionLoading(false);
    }
  }, [assessmentActionLoading, suggestionState, conversationState.selectedConversation, token, onSuccess, onError]);

  // Handle AI assessment proposal rejection
  const handleRejectAssessment = useCallback(() => {
    suggestionState.clearAssessmentSuggestion();
  }, [suggestionState]);

  // Clear action error
  const handleClearActionError = useCallback(() => {
    setAssessmentActionError('');
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    modalState.closeModal('manualAssessment');
    setSelectedAssessmentKey('');
    setAssessmentError('');
  }, [modalState]);

  return (
    <>
      {/* AI Assessment Proposal Card */}
      {suggestionState.suggestionState.assessment && (
        <AiAssessmentProposalCard
          proposal={suggestionState.suggestionState.assessment}
          onForward={handleForwardAssessment}
          onReject={handleRejectAssessment}
          actionLoading={assessmentActionLoading}
          actionError={assessmentActionError}
          onClearError={handleClearActionError}
        />
      )}

      {/* Manual Assessment Modal */}
      <ManualAssessmentModal
        isOpen={modalState.isModalOpen('manualAssessment')}
        onClose={handleCloseModal}
        assessments={availableAssessments}
        selectedKey={selectedAssessmentKey}
        onKeyChange={setSelectedAssessmentKey}
        onSubmit={handleSubmitAssessment}
        loading={assessmentLoading}
        error={assessmentError}
        clientName={clientName}
      />
    </>
  );
};

export default AssessmentManager;